<template>
  <v-card class="acessos-por-dia-da-semana-card card-title pa-3">
    <v-container class="pa-0 card-header">
      <v-card-title class="pa-3 title">Dias da semana de maior acesso</v-card-title>
    </v-container>
    <mf-loading loader="loader" loader-type="table">
      <v-container class="pt-0">
        <apex-chart v-if="series[0].data.length > 0" height="400px" :options="chartOptions" :series="series" />
        <empty-chart v-if="series[0].data.length === 0" />
      </v-container>
      <v-container v-if="series[0].data.length > 0" fluid class="pa-0 navigator-container">
        <chart-navigator
          :selectedPeriod="chartPeriod"
          :chart-index="chartIndex"
          :metrics-length="metricsLength"
          :metrics-period="metricsPeriod"
          @navigateTo="navigateTo"
        />
      </v-container>
    </mf-loading>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import EmptyChart from '@/modules/mobile/desempenho/views/components/EmptyChart'
import ChartNavigator from '@/components/ChartNavigator'
import { QUERY_GET_APP_ACCESSES_BY_DAY_OF_THE_WEEK } from '@/modules/mobile/desempenho/graphql'
import getAppMetricsNavigatorPeriod from '@/services/getAppMetricsNavigatorPeriod'

export default {
  components: {
    ApexChart: VueApexCharts,
    EmptyChart,
    ChartNavigator,
  },
  props: {
    metricsPeriod: String,
  },
  data: () => ({
    chartIndex: 0,
    chartCategories: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'],
  }),
  computed: {
    chartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            dataLabels: {
              position: 'top',
            },
            borderRadius: 3,
            barHeight: '50px',
          },
        },
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: true,
          textAnchor: 'center',
          offsetX: 25,
          style: {
            fontSize: '12px',
            colors: ['#F0F1F2'],
            fontWeight: 'normal',
          },
          background: {
            enabled: true,
            foreColor: '#969EA7',
            padding: 4,
            borderRadius: 2,
          },
          formatter(val) {
            const formattedValue = val >= 1000 ? `${(val / 1000).toFixed(1)} k` : val
            return formattedValue
          },
        },
        xaxis: {
          categories: this.chartCategories,
          labels: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            show: true,
            align: 'right',
            minWidth: 0,
            maxWidth: 50,
            style: {
              colors: '#969EA7',
              fontSize: '12px',
            },
          },
        },
        tooltip: {
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            const registers = series[0][dataPointIndex]

            return `
              <v-card class="d-flex flex-row justify-start pa-1">
                <span><strong>${registers.toLocaleString()} </strong>${registers > 1 || registers === 0 ? 'acessos' : 'acesso'}</span>
              </v-card>
            `
          },
        },
        fill: {
          type: 'solid',
          opacity: 1,
          colors: '#002040',
        },
        grid: {
          yaxis: {
            lines: {
              show: false,
            },
          },
          xaxis: {
            lines: {
              show: true,
            },
          },
        },
      }
    },
    series() {
      if (!this.appAccessesByDayOfTheWeek || this.appAccessesByDayOfTheWeek?.length === 0) {
        return [{ data: [] }]
      }
      const parseWeekDay = (weekDay) => {
        switch (weekDay) {
          case 0:
            return 'Dom'
          case 1:
            return 'Seg'
          case 2:
            return 'Ter'
          case 3:
            return 'Qua'
          case 4:
            return 'Qui'
          case 5:
            return 'Sex'
          case 6:
            return 'Sáb'
          default:
            return ''
        }
      }
      const selectedMetrics = this.appAccessesByDayOfTheWeek[this.chartIndex].metrics
      const series = [
        {
          data: [],
        },
      ]
      this.chartCategories.map((weekDay) => {
        const [metric] = selectedMetrics.filter((item) => parseWeekDay(item.week_day) === weekDay)
        if (metric !== undefined) {
          series[0].data.push(metric.access_count)
        } else {
          series[0].data.push(0)
        }
      })
      return series
    },
    metricsLength() {
      if (!this.appAccessesByDayOfTheWeek) return 0
      return this.appAccessesByDayOfTheWeek.length - 1
    },
    chartPeriod() {
      const currentPeriod =
        this.metricsPeriod === 'MONTHLY' ? this.appAccessesByDayOfTheWeek[this.chartIndex].month : this.appAccessesByDayOfTheWeek[this.chartIndex].year
      const { navigatorPeriod } = getAppMetricsNavigatorPeriod(this.metricsPeriod, currentPeriod)
      return navigatorPeriod
    },
  },
  apollo: {
    appAccessesByDayOfTheWeek: {
      query: QUERY_GET_APP_ACCESSES_BY_DAY_OF_THE_WEEK,
      variables() {
        return { metricsPeriod: this.metricsPeriod }
      },
    },
  },
  watch: {
    metricsPeriod() {
      this.chartIndex = 0
    },
  },
  methods: {
    navigateTo(newIndex) {
      this.chartIndex = newIndex
    },
  },
}
</script>

<style lang="scss" scoped>
.acessos-por-dia-da-semana-card {
  height: 500px;
  position: relative;
}
.card-header {
  height: 30px;
}
p {
  color: $cinza-d10;
  font-size: 12px;
  margin: 0;
}
.chart-container {
  height: 430px;
  position: relative;
}
.chart {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -60%);
}
.table-text {
  color: $cinza-d10;
}
.navigator-container {
  position: absolute;
  bottom: 20px;
  right: 12px;
}
</style>
